// Agent Model Transparency v1 (RC-3) — plain-language labels for the Models card.
//
// The API (`GET /api/agents/{id}/model-resolution`) returns the raw `purpose`
// and its `group`; the UI turns those into human-readable labels + a one-line
// hint. Power users still see the raw purpose in the row tooltip. Kept in one
// place so the card and its tests share the exact copy (mockup-conformant).

export const GROUP_META = {
  conversation: { title: "Conversation", icon: "💬" },
  behind_the_scenes: { title: "Behind the scenes", icon: "⚙️" },
  vision: { title: "Reading images", icon: "🖼️" },
};

// Deterministic section order for the card.
export const GROUP_ORDER = ["conversation", "behind_the_scenes", "vision"];

// purpose → { label, hint }. label is the row headline; hint is the sub-line.
export const PURPOSE_LABELS = {
  chat: { label: "Replies to customers", hint: "the model this agent speaks with" },
  embeddings: { label: "Finds relevant knowledge", hint: "turns text into vectors to search your docs" },
  summarization: { label: "Summarizes documents", hint: "condenses long content for retrieval" },
  intent: { label: "Understands what's being asked", hint: "classifies the request" },
  sentiment: { label: "Reads tone", hint: "detects frustration / escalation" },
  ingestion: { label: "Processes uploaded files", hint: "structures ingested data" },
  voice_amd: { label: "Detects answering machines", hint: "outbound-voice answering-machine detection" },
  ocr: { label: "Extracts text from images", hint: "reads text off image-based documents" },
  figure_caption: { label: "Reads figures", hint: "captions figures cropped from a document" },
  page_caption: { label: "Reads pages", hint: "captions rendered PDF-page screenshots" },
  video_caption: { label: "Reads video keyframes", hint: "captions frames sampled from a video" },
};

// Smart-routing tier row labels (shown under Conversation when enabled).
export const TIER_LABELS = {
  fast: { label: "Fast tier", hint: "simple messages — smart routing" },
  standard: { label: "Standard tier", hint: "everyday messages — smart routing" },
  complex: { label: "Complex tier", hint: "hard / analytical messages — smart routing" },
};

export function purposeLabel(purpose) {
  return PURPOSE_LABELS[purpose] || { label: purpose, hint: purpose };
}

// Source → { text, tone }. tone drives the badge color in ModelsCard.
//   local endpoint (endpoint_id set) → "Local endpoint" (purple)  [most informative]
//   agent_* source                   → "Agent override"  (accent)
//   tenant_ranking                   → "Tenant default"  (neutral)
//   no source (unconfigured)         → "Not configured"  (muted)
export function sourceBadge(row) {
  if (row.endpoint_id) return { text: "Local endpoint", tone: "local" };
  if (row.is_agent_override) return { text: "Agent override", tone: "agent" };
  if (row.source === "tenant_ranking") return { text: "Tenant default", tone: "tenant" };
  return { text: "Not configured", tone: "none" };
}

// provider → { glyph, hue } for the small 2-letter provider chip (mockup style).
const PROVIDER_GLYPHS = {
  openai: { glyph: "AI", hue: "#74e3c3" },
  anthropic: { glyph: "CL", hue: "#e8a37a" },
  vertex: { glyph: "GG", hue: "#7aa8f5" },
  google: { glyph: "GG", hue: "#7aa8f5" },
  bedrock: { glyph: "BR", hue: "#f2b45c" },
  cohere: { glyph: "CO", hue: "#8fd3a3" },
  voyage: { glyph: "VO", hue: "#9db4ff" },
  deepseek: { glyph: "DS", hue: "#b39bff" },
};

export function providerGlyph(provider) {
  if (!provider) return { glyph: "—", hue: "#767ca6" };
  if (provider.startsWith("local_")) return { glyph: "LK", hue: "#b39bff" };
  return PROVIDER_GLYPHS[provider] || { glyph: provider.slice(0, 2).toUpperCase(), hue: "#9aa0c6" };
}
